import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { appService, WorkspaceSubscriptionWithApp } from '../services/app.service';
import { useAppSubscription } from '../contexts/AppSubscriptionContext';

/**
 * Look up the current workspace's subscription for a given app slug.
 * Returns the WorkspaceSubscriptionWithApp record (incl. version + category)
 * or null if the workspace is not subscribed to this app.
 *
 * Usage:
 *   const ats = useAppBySlug('ats');
 *   ats?.app_version
 */
export const useAppBySlug = (slug: string): WorkspaceSubscriptionWithApp | null => {
  const { user, currentWorkspace } = useAuth();
  const { refreshTrigger } = useAppSubscription();
  const [app, setApp] = useState<WorkspaceSubscriptionWithApp | null>(null);

  useEffect(() => {
    const loadApp = async () => {
      if (!user || !currentWorkspace) {
        setApp(null);
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const subscriptions = await appService.getWorkspaceSubscriptions(
          currentWorkspace.id,
          idToken
        );
        setApp(subscriptions.find((s) => s.app_slug === slug) ?? null);
      } catch (error) {
        console.error(`Failed to look up app '${slug}':`, error);
        setApp(null);
      }
    };

    loadApp();
  }, [user, currentWorkspace, refreshTrigger, slug]);

  return app;
};
